import { useEffect, useRef, useCallback } from "react";
import { performTokenRefresh } from "../Network";

/**
 * Hook to proactively refresh the authentication token before it expires.
 * Delegates the actual refresh to the shared performTokenRefresh() from Network,
 * which deduplicates concurrent calls via a single in-flight promise.
 */
const useTokenRefresh = () => {
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);

  const getTokenExpiration = useCallback((token: string | null): number | null => {
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.exp * 1000;
    } catch (error) {
      console.error("Error parsing token:", error);
      return null;
    }
  }, []);

  const refreshToken = useCallback(async (): Promise<boolean> => {
    const result = await performTokenRefresh();
    return result !== null;
  }, []);

  const scheduleRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    const token = localStorage.getItem("token");
    const expiration = getTokenExpiration(token);

    if (!expiration) return;

    const timeUntilRefresh = expiration - Date.now() - (5 * 60 * 1000);

    if (timeUntilRefresh <= 0) {
      refreshToken().then((success) => {
        if (success) {
          scheduleRefresh();
        }
      });
      return;
    }

    refreshTimerRef.current = setTimeout(async () => {
      const success = await refreshToken();
      if (success) {
        scheduleRefresh();
      }
    }, timeUntilRefresh);
  }, [getTokenExpiration, refreshToken]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      scheduleRefresh();
    }

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "token" && e.newValue) {
        scheduleRefresh();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    let activityTimeout: NodeJS.Timeout | null = null;
    const handleActivity = () => {
      if (activityTimeout) clearTimeout(activityTimeout);
      activityTimeout = setTimeout(() => {
        const currentToken = localStorage.getItem("token");
        const expiration = getTokenExpiration(currentToken);
        if (expiration && (expiration - Date.now()) < 5 * 60 * 1000) {
          refreshToken().then((success) => {
            if (success) scheduleRefresh();
          });
        }
      }, 1000);
    };

    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keydown", handleActivity);
    window.addEventListener("click", handleActivity);
    window.addEventListener("scroll", handleActivity);

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      if (activityTimeout) {
        clearTimeout(activityTimeout);
      }
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("click", handleActivity);
      window.removeEventListener("scroll", handleActivity);
    };
  }, [scheduleRefresh, refreshToken, getTokenExpiration]);

  return { refreshToken };
};

export default useTokenRefresh;

